/**
 * Epic #1507 fidelity F6 — a small CANVAS 2D dot-lattice approximation of the source skin's
 * `VANTA.DOTS` background (`.node-design/original-skin/stages/others/welcome/welcome-waves.js`).
 *
 * NOT a port of VANTA/three.js — the real `VANTA.DOTS` effect is a WebGL/three.js scene
 * (~700KB min+gzip vendored, `vendor/three.min.js` + the VANTA plugin) this shell's tight PWA-
 * bootstrap JS budget (~249KB gzip, `system/pwa/scripts/verify-build.mjs`) cannot absorb. This
 * reproduces only the visible surface of the source's own config — `size: 3.2, spacing: 32.0,
 * color/color2/backgroundColor` — as a flat grid of dots on a plain 2D canvas: same spacing/
 * size/palette, no 3D depth, no true wave-field physics. Deliberately minimal (every extra byte
 * here competes with the F2/F3 margin — see the budget note in `verify-build.mjs`).
 *
 * Theme: reads whichever ancestor actually carries the toggled `dark` class per `shell/theme-
 * init.ts` (the shadow HOST when mounted inside `<node-app-shell>`, `document.documentElement`
 * otherwise), re-checked on every redraw rather than via a live subscription.
 */
import { currentMediaPreferences } from "../shell/media-preferences.js";
const SPACING = 32;
const RADIUS = 1.6;
export function mountWavesCanvas(canvas) {
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        return { dispose() { } };
    }
    const host = themeHost(canvas);
    const reduced = currentMediaPreferences().prefersReducedMotion();
    let raf = 0;
    let phase = 0;
    function draw() {
        const parent = canvas.parentElement;
        const dpr = window.devicePixelRatio || 1;
        const width = parent?.clientWidth ?? canvas.clientWidth;
        const height = parent?.clientHeight ?? canvas.clientHeight;
        canvas.width = Math.max(1, Math.round(width * dpr));
        canvas.height = Math.max(1, Math.round(height * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const dark = host.classList.contains("dark");
        ctx.fillStyle = dark ? "#0A0A0A" : "#FFFFFF";
        ctx.fillRect(0, 0, width, height);
        const dotColor = dark ? "#E8E8E8" : "#0A0A0A";
        let index = 0;
        for (let y = 0; y <= height; y += SPACING) {
            for (let x = 0; x <= width; x += SPACING, index++) {
                const accent = index % 17 === 0;
                const r = accent ? RADIUS : RADIUS * (0.7 + 0.3 * Math.sin(phase + index));
                ctx.globalAlpha = accent ? 0.85 : 0.4;
                ctx.fillStyle = accent ? "#DF6662" : dotColor;
                ctx.beginPath();
                ctx.arc(x, y, r, 0, 7);
                ctx.fill();
            }
        }
        ctx.globalAlpha = 1;
    }
    function loop() {
        phase += 0.02;
        draw();
        raf = requestAnimationFrame(loop);
    }
    const resizeObserver = new ResizeObserver(draw);
    if (canvas.parentElement) {
        resizeObserver.observe(canvas.parentElement);
    }
    if (reduced) {
        draw();
    }
    else {
        raf = requestAnimationFrame(loop);
    }
    return {
        dispose() {
            resizeObserver.disconnect();
            if (raf)
                cancelAnimationFrame(raf);
        },
    };
}
function themeHost(node) {
    const root = node.getRootNode();
    if (root instanceof ShadowRoot) {
        return root.host;
    }
    return document.documentElement;
}
//# sourceMappingURL=waves-canvas.js.map